/**
 * Encontra, no catálogo da unidade ATUAL, o tratamento equivalente a um de
 * outra unidade.
 *
 * ─── PORQUÊ ─────────────────────────────────────────────────────────────────
 *
 * Cada unidade tem o seu catálogo. As cópias foram criadas pelo
 * `duplicarCatalogoUnidade.mjs`, que NÃO guarda ligação ao original: são
 * documentos novos, com ids novos. «Destartarização» da SEDE e
 * «Destartarização» da HT2 são dois tratamentos distintos para a base de dados,
 * e podem até ter preços diferentes — é para isso que servem.
 *
 * Logo, ao duplicar um orçamento de uma unidade para outra, comparar ids nunca
 * encontra nada. O que resta é o NOME — que é, aliás, o mesmo critério que o
 * script de duplicação já usa para não criar cópias repetidas.
 *
 * ─── O QUE ISTO NÃO FAZ ─────────────────────────────────────────────────────
 *
 * Não inventa. Se não houver equivalente, devolve `null` e quem chama tem de
 * tratar disso — tipicamente marcando a linha para a pessoa escolher à mão.
 *
 * O que havia antes era um recurso ao PRIMEIRO tratamento da lista:
 *
 *     tratamento: tratamentoCompleto || sortedTratamentos[0]
 *
 * Isso não falhava — produzia um orçamento cheio do tratamento errado, com os
 * preços errados, em silêncio. Um erro visível é sempre melhor do que um
 * palpite invisível.
 */

/** Nomes iguais a menos de maiúsculas, acentos e espaços a mais. */
const normalizar = (texto) =>
  (texto || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ");

/**
 * @param {object} tratamentoOrigem  o tratamento do registo a duplicar
 * @param {Array}  catalogo          os tratamentos da unidade atual
 * @returns {object|null} o equivalente, ou `null` se não houver
 */
export const encontrarTratamentoEquivalente = (tratamentoOrigem, catalogo) => {
  if (!tratamentoOrigem || !Array.isArray(catalogo)) return null;

  const idOrigem = String(
    tratamentoOrigem._id || tratamentoOrigem.id || tratamentoOrigem,
  );

  /* 1. Por id — o caminho normal quando a origem é da MESMA unidade, que é o
        caso mais comum: duplicar um orçamento ali mesmo. */
  const porId = catalogo.find(
    (t) => String(t.id) === idOrigem || String(t._id) === idOrigem,
  );
  if (porId) return porId;

  /* 2. Por nome — quando vem de outra unidade. */
  const nome = normalizar(tratamentoOrigem.designacao);
  if (!nome) return null;

  return catalogo.find((t) => normalizar(t.designacao) === nome) || null;
};

/**
 * Texto a mostrar quando não há equivalente. Nomeia o tratamento em falta: sem
 * o nome, quem lê não sabe qual das linhas tem de corrigir.
 */
export const motivoTratamentoSemEquivalente = (tratamentoOrigem) =>
  `«${tratamentoOrigem?.designacao || "Tratamento"}» não existe no catálogo desta unidade. Escolha o tratamento correspondente.`;
